import { useCallback, useRef, useState } from 'react';
import {
  anchorReferenceFromElement,
  type HoverPopoverAnchorReference,
} from '@components/ui/HoverPopover/HoverPopover.types';
import { useHoverIntent } from '@components/ui/HoverPopover/useHoverIntent';

const CASCADE_OPEN_MS = 150;
const CASCADE_CLOSE_MS = 150;

export interface CascadeState {
  fieldId: string;
  reference: HoverPopoverAnchorReference;
  /**
   * How the editor was opened. Explicit opens (click / Enter / ArrowRight)
   * move focus into the editor and make it sticky against mouse-leave; hover
   * opens must steal neither focus nor persistence.
   */
  openedBy: 'hover' | 'explicit';
}

/**
 * Sub-panel intent for the depth-2 cascade: hovering a root row opens its
 * editor after a short rest, sliding to a neighbouring row swaps instantly
 * (no second delay), leaving closes after a grace period unless the editor
 * was opened explicitly, and entering the editor cancels any pending close.
 * The semantics mirror useKeyedHoverPopover; written locally because the
 * calendar hook's pin/anchor model doesn't fit a menu.
 */
export const useCascade = () => {
  const [state, setState] = useState<CascadeState | null>(null);
  const stateRef = useRef(state);
  stateRef.current = state;

  const { scheduleOpen, scheduleClose, cancelOpen, cancelClose, clearAll } =
    useHoverIntent();

  const closeSub = useCallback(() => {
    clearAll();
    setState(null);
  }, [clearAll]);

  const openNow = useCallback(
    (fieldId: string, anchorEl: HTMLElement) => {
      clearAll();
      setState({
        fieldId,
        reference: anchorReferenceFromElement(anchorEl),
        openedBy: 'explicit',
      });
    },
    [clearAll],
  );

  const onRowEnter = useCallback(
    (fieldId: string, anchorEl: HTMLElement) => {
      cancelClose();
      const current = stateRef.current;
      if (current?.fieldId === fieldId) return;

      const reference = anchorReferenceFromElement(anchorEl);
      if (current) {
        // A different editor is already open — swap instantly. Keep openedBy:
        // an explicit session stays sticky/focused through row swaps.
        cancelOpen();
        setState({ fieldId, reference, openedBy: current.openedBy });
        return;
      }

      scheduleOpen(() => {
        setState({ fieldId, reference, openedBy: 'hover' });
      }, CASCADE_OPEN_MS);
    },
    [cancelClose, cancelOpen, scheduleOpen],
  );

  const scheduleLeaveClose = useCallback(() => {
    cancelOpen();
    if (stateRef.current?.openedBy === 'explicit') return;
    scheduleClose(() => setState(null), CASCADE_CLOSE_MS);
  }, [cancelOpen, scheduleClose]);

  return {
    state,
    /** Root-row hover handlers. */
    onRowEnter,
    onRowLeave: scheduleLeaveClose,
    /** Click / keyboard open. */
    openNow,
    /** Editor-surface pointer handlers. */
    onSubEnter: cancelClose,
    onSubLeave: scheduleLeaveClose,
    closeSub,
  };
};
